import type { BisonEnvelope } from "./bison-contracts.ts";
import { type ContextKey, type Scope, writerDiffersFromLead } from "./context-contracts.ts";

/**
 * Who a rep's answer on a question card applies to. The host decides, per context key; the scope
 * the question was raised at never does.
 *
 * Before this, a typed answer was stored at the pending question's own scope, which the analyst
 * chooses, and nothing on the card said so: a meeting length typed for one lead was told to every
 * later lead in the campaign (docs/2026-09-27-question-card-answer-scope.md). The card, the answer
 * route and `answerRun` now all read this one table, so what the card says and what is written
 * agree by construction.
 *
 * - `choice`: the rep chooses this conversation or every lead in the campaign. The answer itself is
 *   always held for this conversation, which is what lets the waiting run continue; choosing the
 *   campaign also saves a campaign Standing answer, which is visible, editable and removable there.
 *   These are the keys a campaign may override (`CAMPAIGN_OVERRIDABLE_CONTEXT_KEYS`), the only
 *   ones where a conversation's value may sit beside the campaign's without a conflict, less
 *   `event`, whose authoring policy allows only the campaign (`context/field-policy.ts`).
 * - `fixed`: one scope, whatever the question said. Every other key treats two scopes as a conflict
 *   that stops drafting, so a card must never be able to write it at a second one.
 * - `person`: facts about the human who wrote, bound to them the way the time zone question is.
 * - `configuration`: never answered on a card; the campaign page owns it.
 */
export type AnswerScopeRule =
  | { readonly kind: "choice"; readonly default: AnswerChoice }
  | { readonly kind: "fixed"; readonly scope: FixedAnswerScope; readonly reason: FixedScopeReason }
  | { readonly kind: "person" }
  | { readonly kind: "configuration" };

/** The two answers a rep can choose between: this conversation, or every lead in its campaign. */
export type AnswerChoice = "thread" | "campaign";
type FixedAnswerScope = "thread" | "campaign" | "rep";
export type FixedScopeReason =
  | "live"
  | "this_conversation"
  | "one_per_campaign"
  | "campaign_only"
  | "about_you";

const onePerCampaign = { kind: "fixed", scope: "campaign", reason: "one_per_campaign" } as const;
const thisConversation = { kind: "fixed", scope: "thread", reason: "this_conversation" } as const;
const live = { kind: "fixed", scope: "thread", reason: "live" } as const;
const aboutYou = { kind: "fixed", scope: "rep", reason: "about_you" } as const;

/** Exhaustive: a context key added without a rule here does not compile, and a test also checks. */
export const ANSWER_SCOPE_RULES = {
  campaign_goal: { kind: "configuration" },
  next_step_policy: { kind: "configuration" },
  calendar_mode: { kind: "configuration" },
  recipient_timezone: { kind: "person" },
  prospect_name: { kind: "person" },
  rep_timezone: aboutYou,
  rep_name: aboutYou,
  seller_name: onePerCampaign,
  seller_identity: onePerCampaign,
  meeting_hours: onePerCampaign,
  event: { kind: "fixed", scope: "campaign", reason: "campaign_only" },
  // A reply language is a fact about this lead's conversation; a seller who always writes one
  // language says so in Standing answers, not on one lead's card.
  language: thisConversation,
  seller_offer: { kind: "choice", default: "campaign" },
  meeting_duration_minutes: { kind: "choice", default: "campaign" },
  // The card opens with common working hours filled in, not the seller's own, so saving them for
  // every later lead is a choice the rep makes, never the default (after-critique, 2026-09-27).
  scheduling_policy: { kind: "choice", default: "thread" },
  // Dawid's default for the Yes/No card (2026-09-19): an answer to one lead's question is for that
  // conversation until the rep says otherwise.
  approved_facts: { kind: "choice", default: "thread" },
  qualification_context: { kind: "choice", default: "thread" },
  seller_resources: { kind: "choice", default: "thread" },
  reply_guards: { kind: "choice", default: "thread" },
  answers_when_asked: { kind: "choice", default: "thread" },
  availability: live,
  booking_context: live,
  tone_preferences: thisConversation,
  approved_examples: thisConversation,
  feedback: thisConversation,
  commitments: thisConversation,
  follow_up_promises: thisConversation,
} as const satisfies Record<ContextKey, AnswerScopeRule>;

export function answerScopeRule(key: ContextKey): AnswerScopeRule {
  return ANSWER_SCOPE_RULES[key];
}

/** Whether a rep may hold this key's answer for every lead in the campaign as well. */
export function campaignChoiceAllowed(key: ContextKey): boolean {
  return ANSWER_SCOPE_RULES[key].kind === "choice";
}

/**
 * The scope `answerRun` writes a card's answer at.
 *
 * A choice key is written for this conversation: the campaign half of the rep's choice is a Standing
 * answer the route saves, not a second write here. A person-bound key follows the same rule the
 * time zone question uses, so the answer lands where that question will look for it.
 */
export function answerWriteScope(key: ContextKey, input: BisonEnvelope): Scope {
  const rule: AnswerScopeRule = ANSWER_SCOPE_RULES[key];
  switch (rule.kind) {
    case "choice":
      return "thread";
    case "fixed":
      return rule.scope;
    case "configuration":
      return "campaign";
    case "person":
      return writerDiffersFromLead(
        typeof input.lead.email === "string" ? input.lead.email : null,
        input.trigger.from_email_address ?? null,
      )
        ? "person"
        : "lead";
  }
}
